import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from '../utils/helpers';
import { getDeviceId, getDeviceLabel } from '../utils/deviceIdentity';
import { Cookies } from 'react-cookie';
import { toast } from 'react-toastify';
import { store } from '../store';
import {
  setSelectedMerchant,
  clearMerchantState,
  selectMerchantId,
  selectMerchants,
} from '../store/slices/merchantSlice';
import type { RootState } from '../types';

const cookies = new Cookies();

const headers = {
  'Content-Type': 'application/json',
};

// Create an axios instance
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers,
});

// Request Interception – attach auth, selected merchant and device identity
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers.Authorization = `Bearer ${getAccessToken()}`;
    const merchantId = selectMerchantId(store.getState() as RootState);
    if (merchantId) {
      config.headers['X-Merchant-ID'] = merchantId;
    }
    // Per-browser device fingerprint for audit attribution (shared logins).
    // Label is derived automatically from OS/browser + id hash — no user input.
    config.headers['X-Device-Id'] = getDeviceId();
    config.headers['X-Device-Label'] = getDeviceLabel();
    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interception
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: { message?: string } }>) => {
    const { response, config } = error || {};
    const originalRequest = config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = cookies.get('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token found');
        }

        const newAuthToken = await axiosInstance.patch('user/staff/login', {
          refresh_token: refreshToken,
        });

        const accessToken = newAuthToken.data.data.access_token;
        cookies.set('authToken', accessToken, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed, logging out...');
        cookies.remove('authToken', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('isAuthenticated', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    if (response?.status === 403) {
      const merchants = selectMerchants(store.getState() as RootState);
      if (merchants.length > 0) {
        store.dispatch(setSelectedMerchant(merchants[0]));
      } else {
        // No merchants: logout so user can re-auth or see access issue
        toast.error(
          'You do not have access to any merchant. Please contact your administrator.',
          { autoClose: 5000 },
        );
        store.dispatch(clearMerchantState());
        cookies.remove('authToken', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('isAuthenticated', { path: '/' });
        // Delay redirect so the toast is visible before full-page navigation
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    }

    return Promise.reject(error);
  },
);

export { axiosInstance };
